import React, { useRef, useState } from 'react';

import { Input, Toggle } from 'components';
import { useTranslation } from 'react-i18next';

import { TaxAmountType } from 'lane-shared/domains/billingPayments/types';
import {
  TAX_VALUE_CONVERSION,
  TAX_TYPE_OPTIONS,
  DEFAULT_ADMIN_FEE_TAX,
} from 'lane-shared/domains/billingPayments/constants';
import type { AdminFeeTax } from 'application-types';
import { Currency } from 'domains-billing-currency';

import CurrencyInput from 'components/form/CurrencyInput';
import Dropdown from 'components/form/Dropdown';
import { M } from 'components/typography';

import styles from '../styles.scss';

const TRANSLATION_KEYS = {
  header: 'abp.billingAndPaymentSettings.tax.adminFeeRate.header',
  toggleLabel: 'abp.billingAndPaymentSettings.tax.adminFeeRate.toggleLabel',
  defaultRateLabel:
    'abp.billingAndPaymentSettings.tax.adminFeeRate.defaultRateLabel',
} as const;

type AdminFeeTaxRateProps = {
  isAdminFeeTaxEnabled: boolean;
  adminFeeTax: AdminFeeTax | null;
  currency: Currency;
  updateIsAdminFeeTaxEnabled: (value: boolean) => void;
  updateAdminFeeTax: (value: AdminFeeTax) => void;
};

export function AdminFeeTaxRate({
  isAdminFeeTaxEnabled,
  adminFeeTax,
  currency,
  updateIsAdminFeeTaxEnabled,
  updateAdminFeeTax,
}: AdminFeeTaxRateProps) {
  const { t } = useTranslation();
  const resolvedAdminFeeTax = adminFeeTax ?? DEFAULT_ADMIN_FEE_TAX;
  const resolvedRate = resolvedAdminFeeTax.rate / TAX_VALUE_CONVERSION;

  const lastSentRateRef = useRef(resolvedRate);
  const [rateDisplay, setRateDisplay] = useState(String(resolvedRate));

  // Detect external rate changes (from parent, not from our own edits) during render.
  // React supports calling setState here to immediately re-render with corrected state.
  if (lastSentRateRef.current !== resolvedRate) {
    lastSentRateRef.current = resolvedRate;
    setRateDisplay(String(resolvedRate));
  }

  return (
    <div className={styles.SectionContainer}>
      <M mt={2} mb={2} className={styles.FieldLabel}>
        <label>{t(TRANSLATION_KEYS.header)}</label>
      </M>
      <div className={styles.RefundToggleContainer}>
        <Toggle
          value={isAdminFeeTaxEnabled}
          onChange={updateIsAdminFeeTaxEnabled}
          testId="adminFeeTaxToggle"
        />
        <M>{t(TRANSLATION_KEYS.toggleLabel)}</M>
      </div>
      {isAdminFeeTaxEnabled && (
        <div className={styles.AdminFeeTaxRateDefaultSection}>
          <M mt={2} className={styles.FieldLabel}>
            <label>{t(TRANSLATION_KEYS.defaultRateLabel)}</label>
          </M>
          <div className={styles.TaxTypeValueContainer}>
            <Dropdown
              testId="adminFeeTaxTypeDropdown"
              className={styles.TaxTypeDropdown}
              items={TAX_TYPE_OPTIONS}
              value={resolvedAdminFeeTax.taxAmountType as TaxAmountType}
              onChange={option =>
                updateAdminFeeTax({
                  ...resolvedAdminFeeTax,
                  taxAmountType: option.value as AdminFeeTax['taxAmountType'],
                })
              }
            />
            {resolvedAdminFeeTax.taxAmountType ===
              (TaxAmountType.TaxAmountTypePercentage as AdminFeeTax['taxAmountType']) && (
              <Input
                type="number"
                icon="Percent"
                iconRight
                value={rateDisplay}
                onChange={value => {
                  setRateDisplay(value);
                  const numValue = Number(value);
                  if (!Number.isNaN(numValue)) {
                    lastSentRateRef.current = numValue;
                    updateAdminFeeTax({
                      ...resolvedAdminFeeTax,
                      rate: numValue * TAX_VALUE_CONVERSION,
                    });
                  }
                }}
                onBlur={() => {
                  const numValue = parseFloat(rateDisplay);
                  setRateDisplay(String(Number.isNaN(numValue) ? 0 : numValue));
                }}
                testId="adminFeeTaxAmountValueInput"
                className={styles.TaxValue}
              />
            )}
            {resolvedAdminFeeTax.taxAmountType ===
              (TaxAmountType.TaxAmountTypeFixed as AdminFeeTax['taxAmountType']) && (
              <CurrencyInput
                value={resolvedAdminFeeTax.rate}
                min={0}
                currency={currency}
                onValueChange={value =>
                  updateAdminFeeTax({
                    ...resolvedAdminFeeTax,
                    rate: value,
                  })
                }
                testId="adminFeeTaxAmountValueInput"
                className={styles.TaxValue}
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
}
